/**
 * Scripts globaux du thème (JS natif, sans dépendance).
 * - Loader
 * - Apparition au défilement
 * - En-tête : état au défilement, menu mobile, recherche
 * - <carousel-slider> : carrousel accessible basé sur scroll-snap
 */

(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------- Loader */
  const loader = document.getElementById('PageLoader');
  if (loader && !root.classList.contains('no-loader')) {
    const start = performance.now();
    const minDuration = reduceMotion ? 0 : 500;
    const hide = () => {
      const wait = Math.max(0, minDuration - (performance.now() - start));
      setTimeout(() => {
        loader.classList.add('is-hidden');
        setTimeout(() => (loader.hidden = true), 800); // stoppe ses animations
        try {
          sessionStorage.setItem('loader-seen', '1');
        } catch (e) {}
      }, wait);
    };
    // On n'attend pas les images : le loader part dès que la page est prête
    if (document.readyState !== 'loading') hide();
    else document.addEventListener('DOMContentLoaded', hide, { once: true });
  }

  /* ---------------------------------------------------------- Apparition au défilement */
  const initReveal = (scope = document) => {
    const items = scope.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!root.classList.contains('js-animate') || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
  };

  document.addEventListener('DOMContentLoaded', () => initReveal());

  // Dans l'éditeur de thème, les sections rechargées doivent réapparaître tout de suite.
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

  /* ---------------------------------------------------------- Parallaxe douce des bannières */
  if (!reduceMotion && root.classList.contains('js-animate')) {
    let ticking = false;
    const parallax = () => {
      document.querySelectorAll('[data-parallax]').forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.transform = `translate3d(0, ${progress * -8}%, 0)`;
      });
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) requestAnimationFrame(parallax);
        ticking = true;
      },
      { passive: true }
    );
    document.addEventListener('DOMContentLoaded', parallax);
  }

  /* ---------------------------------------------------------- Barre d'annonce (rotation sur mobile) */
  const initAnnouncements = () => {
    document.querySelectorAll('[data-announcement]').forEach((track) => {
      const items = [...track.children];
      if (items.length < 2 || track.dataset.ready) return;
      track.dataset.ready = 'true';
      let index = 0;
      items[0].classList.add('is-active');
      track.classList.add('is-rotating');
      if (reduceMotion) return;
      setInterval(() => {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, 4500);
    });
  };
  document.addEventListener('DOMContentLoaded', initAnnouncements);
  document.addEventListener('shopify:section:load', initAnnouncements);

  /* ---------------------------------------------------------- Verrouillage du défilement */
  const lockScroll = (lock) => root.classList.toggle('is-locked', lock);
  window.themeLockScroll = lockScroll;
})();

/* ---------------------------------------------------------- Prix et panier (API Ajax de Shopify) */
window.theme = window.theme || {};

/** Formate un montant en centimes selon le format de devise de la boutique. */
window.theme.formatMoney = (cents, format = window.theme.moneyFormat || '{{amount_with_comma_separator}} €') => {
  const value = Number(cents) / 100;
  const formatNumber = (decimals, thousands, decimal) => {
    const [int, dec] = value.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return formatNumber(0, ',', '.');
      case 'amount_with_comma_separator':
        return formatNumber(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatNumber(0, '.', ',');
      case 'amount_with_space_separator':
        return formatNumber(2, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return formatNumber(2, "'", '.');
      default:
        return formatNumber(2, ',', '.');
    }
  });
};

window.theme.cart = {
  async request(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || window.theme.strings?.error);
    return data;
  },

  async refresh() {
    const cart = await this.request(`${window.theme.routes.cart}.js`);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  },

  async add(formData) {
    const item = await this.request(`${window.theme.routes.cartAdd}.js`, { method: 'POST', body: formData });
    const cart = await this.refresh();
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { item, cart } }));
    return cart;
  },

  async change(line, quantity) {
    const cart = await this.request(`${window.theme.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  },
};

/* Ajout rapide depuis les cartes produits */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-quick-add]');
  if (!form) return;
  event.preventDefault();
  const label = form.querySelector('[data-quick-label]');
  const initial = label?.textContent;
  form.classList.add('is-loading');
  try {
    await window.theme.cart.add(new FormData(form));
    if (label) label.textContent = window.theme.strings.added;
    if (document.body.dataset.cartType === 'page') window.location.href = window.theme.routes.cart;
  } catch (error) {
    if (label) label.textContent = error.message;
  } finally {
    form.classList.remove('is-loading');
    setTimeout(() => label && (label.textContent = initial), 2200);
  }
});

/* Ajout en un clic hors formulaire (suggestions du panier) */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-add-variant]');
  if (!button) return;
  event.preventDefault();
  const label = button.querySelector('[data-quick-label]');
  button.classList.add('is-loading');
  button.disabled = true;
  try {
    const data = new FormData();
    data.append('id', button.dataset.addVariant);
    data.append('quantity', '1');
    await window.theme.cart.add(data);
    if (document.body.dataset.cartType === 'page') window.location.reload();
  } catch (error) {
    if (label) label.textContent = error.message;
    button.disabled = false;
  } finally {
    button.classList.remove('is-loading');
  }
});

/* Met à jour le montant et le compteur du panier dans l'en-tête */
document.addEventListener('cart:updated', (event) => {
  const { cart } = event.detail;
  document.querySelectorAll('[data-cart-total]').forEach((el) => (el.textContent = window.theme.formatMoney(cart.total_price)));
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.hidden = cart.item_count === 0;
  });
});

/* ---------------------------------------------------------- En-tête */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    this.setupDrawer('[data-menu-open]', '[data-menu-close]', '#MenuDrawer');
    this.setupDrawer('[data-search-open]', '[data-search-close]', '#SearchDrawer', '#SearchInput');

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });

    this.setupPredictiveSearch();
  }

  /* Recherche instantanée : résultats dès 2 caractères, sans recharger la page */
  setupPredictiveSearch() {
    const input = this.querySelector('#SearchInput');
    const results = this.querySelector('[data-predictive-results]');
    if (!input || !results || !window.theme?.routes?.predictiveSearch) return;
    let timer;
    let controller;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const terms = input.value.trim();
      if (terms.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: terms,
          'resources[type]': 'product,collection,page',
          'resources[limit]': '4',
          section_id: 'predictive-search',
        });
        try {
          const html = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: controller.signal }).then((r) => r.text());
          const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-content]');
          results.innerHTML = content ? content.outerHTML : '';
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        }
      }, 250);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    const scrolled = window.scrollY > 10;
    this.classList.toggle('is-scrolled', scrolled);
  }

  setupDrawer(openSelector, closeSelector, panelSelector, focusSelector) {
    const panel = this.querySelector(panelSelector);
    if (!panel) return;
    const openers = this.querySelectorAll(openSelector);

    const open = () => {
      this.closeAll();
      panel.hidden = false;
      void panel.offsetWidth; // force le rendu pour que la transition démarre
      panel.classList.add('is-open');
      openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      window.themeLockScroll(true);
      this.activeOpener = document.activeElement;
      const target = focusSelector ? panel.querySelector(focusSelector) : panel.querySelector('a, button');
      target?.focus({ preventScroll: true });
    };

    const close = () => {
      if (panel.hidden) return;
      panel.classList.remove('is-open');
      openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      window.themeLockScroll(false);
      setTimeout(() => (panel.hidden = true), 450);
      this.activeOpener?.focus({ preventScroll: true });
    };

    panel.closePanel = close;
    openers.forEach((btn) => btn.addEventListener('click', open));
    panel.querySelectorAll(closeSelector).forEach((btn) => btn.addEventListener('click', close));
  }

  closeAll() {
    this.querySelectorAll('[data-panel]').forEach((panel) => panel.closePanel?.());
  }
}
customElements.define('site-header', SiteHeader);

/* ---------------------------------------------------------- Carrousel */
class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.carousel__track');
    if (!this.track) return;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.progress = this.querySelector('.carousel__progress');

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  go(direction) {
    const slide = this.track.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    this.track.scrollBy({ left: direction * step * visible, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const scrollable = max > 2;
    this.toggleAttribute('data-static', !scrollable);
    if (this.prev) this.prev.disabled = !scrollable || scrollLeft <= 2;
    if (this.next) this.next.disabled = !scrollable || scrollLeft >= max - 2;
    if (this.progress) {
      const ratio = scrollable ? clientWidth / scrollWidth : 1;
      const offset = scrollable ? (scrollLeft / max) * (1 / ratio - 1) * 100 : 0;
      this.progress.style.setProperty('--progress-width', `${ratio * 100}%`);
      this.progress.style.setProperty('--progress-offset', `${offset}%`);
    }
  }
}
customElements.define('carousel-slider', CarouselSlider);

/* ---------------------------------------------------------- Favoris et parfums vus récemment (stockés dans le navigateur) */
(() => {
  const read = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch (e) {
      return [];
    }
  };
  const write = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {}
  };

  /** Récupère les cartes produits rendues par le modèle alternatif `product.card` */
  const fetchCards = async (handles) => {
    const root = window.theme.routes.root.replace(/\/$/, '');
    const cards = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}/products/${encodeURIComponent(handle)}?view=card`)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      )
    );
    return cards.filter((html) => html.includes('product-card'));
  };
  window.theme.fetchCards = fetchCards;

  const WISH_KEY = 'nadara:wishlist';
  const wishlist = {
    get: () => read(WISH_KEY),
    has: (handle) => read(WISH_KEY).includes(handle),
    toggle(handle) {
      const list = read(WISH_KEY);
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(handle);
      write(WISH_KEY, list.slice(0, 60));
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list } }));
      return index === -1;
    },
  };
  window.theme.wishlist = wishlist;

  const syncWishButtons = (scope = document) => {
    const list = wishlist.get();
    scope.querySelectorAll('[data-wish]').forEach((button) => {
      const active = list.includes(button.dataset.wish);
      button.setAttribute('aria-pressed', String(active));
    });
    document.querySelectorAll('[data-wish-count]').forEach((el) => {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  };
  window.theme.syncWishButtons = syncWishButtons;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wish]');
    if (!button) return;
    event.preventDefault();
    const added = wishlist.toggle(button.dataset.wish);
    if (added) {
      button.classList.remove('is-popping');
      void button.offsetWidth;
      button.classList.add('is-popping');
    }
  });
  document.addEventListener('wishlist:updated', () => syncWishButtons());
  document.addEventListener('DOMContentLoaded', () => syncWishButtons());
  document.addEventListener('shopify:section:load', (event) => syncWishButtons(event.target));
  window.addEventListener('storage', (event) => event.key === WISH_KEY && syncWishButtons());

  const RECENT_KEY = 'nadara:recent';
  window.theme.recentlyViewed = {
    get: () => read(RECENT_KEY),
    add(handle) {
      if (!handle) return;
      const list = read(RECENT_KEY).filter((h) => h !== handle);
      list.unshift(handle);
      write(RECENT_KEY, list.slice(0, 12));
    },
  };
})();

/* <product-list-loader> : affiche une liste de cartes produits à partir des favoris ou de l'historique */
class ProductListLoader extends HTMLElement {
  async connectedCallback() {
    const source = this.dataset.source;
    const limit = Number(this.dataset.limit) || 12;
    let handles = source === 'wishlist' ? window.theme.wishlist.get() : window.theme.recentlyViewed.get();
    if (this.dataset.exclude) handles = handles.filter((h) => h !== this.dataset.exclude);
    handles = handles.slice(0, limit);

    const grid = this.querySelector('[data-product-list]');
    const empty = this.querySelector('[data-product-list-empty]');
    if (!handles.length) {
      if (empty) empty.hidden = false;
      return;
    }
    const cards = await window.theme.fetchCards(handles);
    if (!cards.length) {
      if (empty) empty.hidden = false;
      return;
    }
    grid.innerHTML = cards.map((html) => `<li>${html}</li>`).join('');
    this.hidden = false;
    this.querySelectorAll('[hidden][data-product-list-content]').forEach((el) => (el.hidden = false));
    grid.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    window.theme.syncWishButtons(this);

    // Sur la page favoris : retire la carte quand on enlève le cœur
    if (source === 'wishlist') {
      document.addEventListener('wishlist:updated', () => {
        const list = window.theme.wishlist.get();
        grid.querySelectorAll('[data-wish]').forEach((button) => {
          if (!list.includes(button.dataset.wish)) button.closest('li')?.remove();
        });
        if (!grid.children.length && empty) empty.hidden = false;
      });
    }
  }
}
customElements.define('product-list-loader', ProductListLoader);

/* Sélecteur de langue : changer de langue recharge la page dans la langue choisie */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-locale-select]');
  if (select) select.form.submit();
});
